import { apiUrl } from './api';
import { adminApiHeaders } from './adminApiAuth';

/** Resultado do parsing de link do Google Maps. */
export type MapsLinkResult = { lat: number; lng: number; zoom?: number } | null;

/** Obtém lat/lng pelo Place ID (usado na importação). */
export async function fetchPlaceCoords(placeId: string): Promise<MapsLinkResult> {
  const id = (placeId ?? '').trim();
  if (!id) return null;
  try {
    const res = await fetch(apiUrl(`/api/admin/place-coords?placeId=${encodeURIComponent(id)}`), {
      headers: adminApiHeaders(),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { lat?: number; lng?: number };
    const lat = typeof data.lat === 'number' ? data.lat : null;
    const lng = typeof data.lng === 'number' ? data.lng : null;
    if (lat != null && lng != null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng, zoom: 16 };
    }
    return null;
  } catch {
    return null;
  }
}

/** Resolve URL do Maps via backend (mais confiável para cid= e links que exigem fetch). */
export async function resolveMapsUrlViaApi(url: string): Promise<MapsLinkResult> {
  const t = (url ?? '').trim();
  if (!t || !t.startsWith('http')) return null;
  try {
    const res = await fetch(apiUrl('/api/admin/resolve-maps-url'), {
      method: 'POST',
      headers: adminApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ url: t }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { lat?: number; lng?: number };
    const lat = typeof data.lat === 'number' ? data.lat : null;
    const lng = typeof data.lng === 'number' ? data.lng : null;
    if (lat != null && lng != null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng, zoom: 16 };
    }
    return null;
  } catch {
    return null;
  }
}

/** Verifica se o texto parece ser um link do Google Maps. */
export function looksLikeMapsLink(text: string): boolean {
  const t = (text ?? '').trim();
  return t.startsWith('http') && (t.includes('google') || t.includes('goo.gl') || t.includes('maps.app'));
}

/** Indica se a URL precisa de fetch para extrair coords (ex: cid=, short links). */
function needsFetchToResolve(url: string): boolean {
  const t = url.trim().toLowerCase();
  return (
    t.includes('goo.gl') ||
    t.includes('maps.app') ||
    (t.includes('maps.google') && !/@[-.\d]+,[-.\d]+/.test(t) && !/!3d[-.\d]+!4d[-.\d]+/.test(t)) ||
    (t.includes('google.com/maps') && !/@[-.\d]+,[-.\d]+/.test(t) && !/!3d[-.\d]+!4d[-.\d]+/.test(t))
  );
}

/** Extrai coordenadas e zoom de link do Google Maps (goo.gl, maps.app, google.com/maps, cid=). */
export async function parseGoogleMapsLink(url: string): Promise<MapsLinkResult> {
  const t = (url ?? '').trim();
  if (!t) return null;
  let resolvedUrl = t;

  if (needsFetchToResolve(t)) {
    // Priorizar API backend (sem CORS, mais confiável para cid= e redirects)
    const apiResult = await resolveMapsUrlViaApi(t);
    if (apiResult) return apiResult;

    /** Vários leitores HTTP (links curtos bloqueiam CORS no browser). */
    const fetchPageTextViaPublicReaders = async (url: string): Promise<string | null> => {
      const u = url.trim();
      if (!u.startsWith('http')) return null;
      const tryFetch = async (src: string): Promise<string | null> => {
        try {
          const res = await fetch(src);
          if (!res.ok) return null;
          const body = await res.text();
          return body && body.length > 80 ? body : null;
        } catch {
          return null;
        }
      };
      const candidates = [
        `https://corsproxy.io/?${encodeURIComponent(u)}`,
        `https://r.jina.ai/${u}`,
        `https://r.jina.ai/${encodeURIComponent(u)}`,
      ];
      for (const src of candidates) {
        const body = await tryFetch(src);
        if (body) return body;
      }
      return null;
    };

    const tryProxy = async (base: string): Promise<string> => {
      const u = `${base}${encodeURIComponent(t)}`;
      const res = await fetch(u, { method: 'GET' });
      if (!res.ok) throw new Error('Proxy failed');
      return res.text();
    };
    const tryProxyJson = async (base: string): Promise<string> => {
      const res = await fetch(`${base}${encodeURIComponent(t)}`);
      if (!res.ok) throw new Error('Proxy failed');
      const json = (await res.json()) as { contents?: string };
      return json?.contents ?? '';
    };
    const parseCoords = (match: RegExpMatchArray, latIdx: number, lngIdx: number): MapsLinkResult => {
      const lat = parseFloat(match[latIdx]);
      const lng = parseFloat(match[lngIdx]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180)
        return null;
      const zoom = match[3] ? parseInt(match[3], 10) : 16;
      return { lat, lng, zoom: Math.min(21, Math.max(0, zoom)) };
    };
    const extractFromHtml = (html: string): MapsLinkResult => {
      const canonical =
        html.match(/<link[^>]+rel=["']canonical["'][^>]+href=["']([^"']+)["']/i) ??
        html.match(/href=["']([^"']+)"[^>]*rel=["']canonical["']/i);
      const ogUrl =
        html.match(/<meta[^>]+property=["']og:url["'][^>]+content=["']([^"']+)["']/i) ??
        html.match(/content=["']([^"']+)["'][^>]+property=["']og:url["']/i);
      for (const m of [canonical?.[1], ogUrl?.[1]]) {
        if (m) {
          const place = m.match(/\/place\/[^/]+\/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?z?/);
          if (place) return parseCoords(place, 1, 2);
          const at = m.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?/);
          if (at) return parseCoords(at, 1, 2);
          const d3d = m.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
          if (d3d) return parseCoords(d3d, 1, 2);
        }
      }
      const placePatterns: Array<{ m: RegExp; latIdx: number; lngIdx: number }> = [
        { m: /\/place\/[^/]+\/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?/, latIdx: 1, lngIdx: 2 },
        { m: /google\.com\/maps\/place\/[^"'\s]*@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?/, latIdx: 1, lngIdx: 2 },
      ];
      for (const { m, latIdx, lngIdx } of placePatterns) {
        const match = html.match(m);
        if (match) {
          const r = parseCoords(match, latIdx, lngIdx);
          if (r) return r;
        }
      }
      const fallback: Array<{ m: RegExp; latIdx: number; lngIdx: number }> = [
        { m: /@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?/, latIdx: 1, lngIdx: 2 },
        { m: /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/, latIdx: 1, lngIdx: 2 },
        { m: /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/, latIdx: 1, lngIdx: 2 },
        { m: /"lat":\s*(-?\d+\.\d+)[^}]*"lng":\s*(-?\d+\.\d+)/, latIdx: 1, lngIdx: 2 },
        { m: /"lng":\s*(-?\d+\.\d+)[^}]*"lat":\s*(-?\d+\.\d+)/, latIdx: 2, lngIdx: 1 },
      ];
      for (const { m, latIdx, lngIdx } of fallback) {
        const match = html.match(m);
        if (match) {
          const r = parseCoords(match, latIdx, lngIdx);
          if (r) return r;
        }
      }
      return null;
    };

    const extractCoordsFromMapsUrlsInText = (text: string): MapsLinkResult => {
      const urlRe = /https?:\/\/(?:www\.)?google\.com\/maps[^\s)\]'"<>]*/gi;
      let um: RegExpExecArray | null;
      while ((um = urlRe.exec(text)) !== null) {
        const piece = um[0];
        const at = piece.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?/);
        if (at) {
          const r = parseCoords(at, 1, 2);
          if (r) return r;
        }
        const d3 = piece.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
        if (d3) {
          const r = parseCoords(d3, 1, 2);
          if (r) return r;
        }
      }
      return null;
    };

    const extractPlaceIdFromText = (text: string): string | null => {
      const m = text.match(/\b(ChIJ[A-Za-z0-9_-]{20,32})\b/);
      return m?.[1] ?? null;
    };

    const resolveFromHtmlOrText = async (raw: string): Promise<MapsLinkResult> => {
      const fromHtml = extractFromHtml(raw);
      if (fromHtml) return fromHtml;
      const fromUrls = extractCoordsFromMapsUrlsInText(raw);
      if (fromUrls) return fromUrls;
      const pid = extractPlaceIdFromText(raw);
      if (pid) {
        const pc = await fetchPlaceCoords(pid);
        if (pc) return pc;
      }
      return null;
    };

    const readerText = await fetchPageTextViaPublicReaders(t);
    if (readerText) {
      const fromReader = await resolveFromHtmlOrText(readerText);
      if (fromReader) return fromReader;
    }

    try {
      const fetchCorsproxy = async (): Promise<string> => {
        const res = await fetch(`https://corsproxy.io/?${encodeURIComponent(t)}`);
        if (!res.ok) throw new Error('corsproxy failed');
        return res.text();
      };
      const fetchCorsEu = async (): Promise<string> => {
        const res = await fetch(`https://cors.eu.org/${encodeURIComponent(t)}`);
        if (!res.ok) throw new Error('cors.eu failed');
        return res.text();
      };
      const proxies: Array<() => Promise<string>> = [
        fetchCorsproxy,
        fetchCorsEu,
        () => tryProxy('https://api.allorigins.win/raw?url='),
        () => tryProxyJson('https://api.allorigins.win/get?url='),
      ];
      let html = '';
      for (const fn of proxies) {
        try {
          html = await fn();
          if (html && html.length > 100) break;
        } catch {
          continue;
        }
      }
      if (html) {
        const result = await resolveFromHtmlOrText(html);
        if (result) return result;
      }
    } catch {
      // fall through
    }
    try {
      const res = await fetch(t, { method: 'GET', redirect: 'follow', mode: 'cors' });
      resolvedUrl = res.url;
    } catch {
      resolvedUrl = t;
    }
  }

  const m1 = resolvedUrl.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+))?/);
  if (m1) {
    const lat = parseFloat(m1[1]);
    const lng = parseFloat(m1[2]);
    const zoom = m1[3] ? parseInt(m1[3], 10) : 16;
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng, zoom: Math.min(21, Math.max(0, zoom)) };
    }
  }
  const m2 = resolvedUrl.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (m2) {
    const lat = parseFloat(m2[1]);
    const lng = parseFloat(m2[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng, zoom: 16 };
    }
  }
  const m3 = resolvedUrl.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (m3) {
    const lat = parseFloat(m3[1]);
    const lng = parseFloat(m3[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng, zoom: 16 };
    }
  }
  const coordMatch = t.match(/^(-?\d{1,3}\.\d+)\s*[,;\s]\s*(-?\d{1,3}\.\d+)\s*$/);
  if (coordMatch) {
    const lat = parseFloat(coordMatch[1]);
    const lng = parseFloat(coordMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng, zoom: 16 };
    }
  }
  return null;
}
